import React, { useState, useEffect, useMemo } from 'react';
import { X, Info } from 'lucide-react';

const BottlingItemModal = ({ 
  show, 
  onClose, 
  onSave, 
  item, 
  wines, 
  materialsByType,
  brands,
  fancifulNames,
  selectedMaterials,
  isEditing,
  calculateEstimatedCases // Add this parameter
}) => {
  const [currentItem, setCurrentItem] = useState({...item});
  const [selectedWineDetails, setSelectedWineDetails] = useState(null);
  const bottleType = materialsByType.bottles.find(b => b.materialID === parseInt(currentItem.bottleID));
  const isLargeFormat = bottleType && parseFloat(bottleType.bottleVolume) >= 3000;
  const caseLabel = isLargeFormat ? 'Estimated Units' : 'Estimated Cases';

  // Add a useEffect to calculate things when form values change
  useEffect(() => {
    setCurrentItem({...item});
    if (item.wineID) {
      const selectedWine = wines.find(w => w.wineID === parseInt(item.wineID));
      setSelectedWineDetails(selectedWine);
    }
  }, [item, wines]);

  const handleWineSelection = (wineId) => {
    const selectedWine = wines.find(w => w.wineID === parseInt(wineId));
    setSelectedWineDetails(selectedWine);
    setCurrentItem(prev => ({
      ...prev,
      wineID: wineId,
      finalAlc: selectedWine?.finalAlc || ''
    }));
  };

  // Compute materials needed directly in the modal
    const computedMaterials = useMemo(() => {
        if (!currentItem.bottleID || !currentItem.gallonsPlanned) {
        return [];
        }
    
        const result = [];
        const bottleId = parseInt(currentItem.bottleID);
        const gallonsPlanned = parseFloat(currentItem.gallonsPlanned);
        const casesEstimated = calculateEstimatedCases(gallonsPlanned, bottleId);
        
        // Find materials in the material store
        const bottle = materialsByType.bottles.find(b => b.materialID === bottleId);
        const corkId = currentItem.corkID ? parseInt(currentItem.corkID) : null;
        const capsuleId = currentItem.capsuleID ? parseInt(currentItem.capsuleID) : null;
        const labelId = currentItem.labelID ? parseInt(currentItem.labelID) : null;
        
        const cork = corkId ? materialsByType.corks.find(c => c.materialID === corkId) : null;
        const capsule = capsuleId ? materialsByType.capsules.find(c => c.materialID === capsuleId) : null;
        const label = labelId ? materialsByType.labels.find(l => l.materialID === labelId) : null;
        
        // Calculate bottles needed based on unit of measure
        if (bottle) {
        const unitLower = (bottle.unitOfMeasure || "").toLowerCase();
        const isCaseUnit = unitLower === 'case' || unitLower === 'cases';
        const bottlesPerCase = bottle.casePack || 12; // Default to 12 if casePack not specified
        
        // If bottles are tracked as cases, we need cases; otherwise individual bottles
        const required = isCaseUnit 
            ? casesEstimated 
            : casesEstimated * bottlesPerCase;
        
        result.push({
            id: bottle.materialID,
            name: bottle.name,
            required,
            available: parseFloat(bottle.currentStock),
            unit: bottle.unitOfMeasure || "Each",
            enoughStock: parseFloat(bottle.currentStock) >= required
        });
        }
        
        // Calculate corks needed (always individual)
        if (cork) {
        const required = casesEstimated * 12;
        result.push({
            id: cork.materialID,
            name: cork.name,
            required,
            available: parseFloat(cork.currentStock),
            unit: cork.unitOfMeasure || "Each",
            enoughStock: parseFloat(cork.currentStock) >= required
        });
        }
        
        // Calculate capsules needed (always individual)
        if (capsule) {
        const required = casesEstimated * 12;
        result.push({
            id: capsule.materialID,
            name: capsule.name,
            required,
            available: parseFloat(capsule.currentStock),
            unit: capsule.unitOfMeasure || "Each",
            enoughStock: parseFloat(capsule.currentStock) >= required
        });
        }
        
        // Calculate labels needed (always individual)
        if (label) {
        const required = casesEstimated * 12;
        result.push({
            id: label.materialID,
            name: label.name,
            required,
            available: parseFloat(label.currentStock),
            unit: label.unitOfMeasure || "Each",
            enoughStock: parseFloat(label.currentStock) >= required
        });
        }
        
        return result;
    }, [currentItem, materialsByType, calculateEstimatedCases]);

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditing ? 'Edit Bottling Plan Item' : 'Add Item to Bottling Plan'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                onSave(currentItem);
              }}>
                {/* Wine Selection */}
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Wine</label>
                    <select
                      className="form-select"
                      value={currentItem.wineID}
                      onChange={(e) => handleWineSelection(e.target.value)}
                      required
                    >
                      <option value="">Select Wine</option>
                      {wines.map(wine => (
                        <option key={wine.wineID} value={wine.wineID}>
                          {wine.vintage} {wine.varietal} - {wine.grower} ({wine.totalGallons} gal)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Wine Details */}
                {selectedWineDetails && (
                  <div className="row mb-3 bg-light p-3 rounded">
                    <div className="col-md-2">
                      <label className="form-label">Vintage</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedWineDetails.vintage}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Varietal</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedWineDetails.varietal}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Appellation</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedWineDetails.appellation}
                        readOnly
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Grower</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedWineDetails.grower}
                        readOnly
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Final Alc %</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={currentItem.finalAlc}
                        onChange={(e) => setCurrentItem({
                          ...currentItem,
                          finalAlc: e.target.value
                        })}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Brand and Fanciful */}
                {selectedWineDetails && (
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Brand</label>
                      <select
                        className="form-select"
                        value={currentItem.brandID}
                        onChange={(e) => setCurrentItem({
                          ...currentItem,
                          brandID: e.target.value
                        })}
                        required
                      >
                        <option value="">Select Brand</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Fanciful Name</label>
                      <select
                        className="form-select"
                        value={currentItem.fancifulNameID}
                        onChange={(e) => setCurrentItem({
                          ...currentItem,
                          fancifulNameID: e.target.value
                        })}
                      >
                        <option value="">Select Fanciful Name</option>
                        {fancifulNames.map(name => (
                          <option key={name.id} value={name.id}>
                            {name.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Version</label>
                      <select
                        className="form-select"
                        value={currentItem.version}
                        onChange={(e) => setCurrentItem({
                          ...currentItem,
                          version: e.target.value
                        })}
                      >
                        <option value="">No Version</option>
                        {Array.from({length: 20}, (_, i) => i + 1).map(num => (
                          <option key={num} value={`v${num}`}>v{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Gallons and Bottles */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Gallons Planned</label>
                    <input
                      type="number"
                      className="form-control"
                      value={currentItem.gallonsPlanned}
                      onChange={(e) => setCurrentItem({
                        ...currentItem,
                        gallonsPlanned: e.target.value
                      })}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-md-6">
                  <label className="form-label">{caseLabel}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={calculateEstimatedCases(currentItem.gallonsPlanned, currentItem.bottleID)}
                    readOnly
                    disabled
                  />
                </div>
                </div>

                {/* Packaging Materials */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Bottle Type</label>
                    <select
                      className="form-select"
                      value={currentItem.bottleID}
                      onChange={(e) => setCurrentItem({ ...currentItem, bottleID: e.target.value })}
                      required
                    >
                      <option value="">Select Bottle</option>
                      {materialsByType.bottles.map(bottle => (
                        <option 
                          key={bottle.materialID} 
                          value={bottle.materialID}
                          disabled={bottle.currentStock <= 0}
                        >
                          {bottle.name} ({bottle.bottleVolume}ml) - {bottle.currentStock} {bottle.unitOfMeasure || "Each"} in stock
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cork Type</label>
                    <select
                      className="form-select"
                      value={currentItem.corkID}
                      onChange={(e) => setCurrentItem({ ...currentItem, corkID: e.target.value })}
                    >
                      <option value="">Select Cork</option>
                      {materialsByType.corks.map(cork => (
                        <option key={cork.materialID} value={cork.materialID}>
                          {cork.name} - {cork.printStyle || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Capsule</label>
                    <select
                      className="form-select"
                      value={currentItem.capsuleID}
                      onChange={(e) => setCurrentItem({ ...currentItem, capsuleID: e.target.value })}
                    >
                      <option value="">Select Capsule</option>
                      {materialsByType.capsules.map(capsule => (
                        <option key={capsule.materialID} value={capsule.materialID}>
                          {capsule.name} - {capsule.color || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Label</label>
                    <select
                        className="form-select"
                        value={currentItem.labelID}
                        onChange={(e) => setCurrentItem({ ...currentItem, labelID: e.target.value })}
                    >
                        <option value="">Select Label</option>
                        {materialsByType.labels.map(label => (
                        <option key={label.materialID} value={label.materialID}>
                            {label.name} - {label.labelStyle || ''} 
                            (Will be ordered specifically for this bottling)
                        </option>
                        ))}
                    </select>
                    <div className="form-text">
                        Labels will be ordered specifically for this bottling with a buffer of 50 extra labels
                    </div>
                    </div>
                </div>

                {/* Materials Stock */}
                <div className="mb-3">
                  <h6>Materials Requirements</h6>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th className="text-end">Required</th>
                        <th className="text-end">Available</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computedMaterials.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            {currentItem.bottleID && currentItem.gallonsPlanned 
                              ? 'No materials found for this configuration'
                              : 'Select wine, gallons planned, and bottle type to see material requirements'}
                          </td>
                        </tr>
                      ) : (
                        computedMaterials.map(material => (
                          <tr key={material.id}>
                            <td>{material.name}</td>
                            <td className="text-end">{material.required} {material.unit}</td>
                            <td className="text-end">{material.available} {material.unit}</td>
                            <td className="text-center">
                              <span className={`badge ${
                                material.enoughStock
                                  ? 'bg-success' 
                                  : 'bg-danger'
                              }`}>
                                {material.enoughStock 
                                  ? 'In Stock' 
                                  : 'Insufficient'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div className="alert alert-info py-2 mt-2">
                    <small>
                      <Info size={14} className="me-1" />
                      Materials are only reserved during planning and will be deducted from inventory when the plan is finalized.
                    </small>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={currentItem.notes || ''}
                    onChange={(e) => setCurrentItem({ ...currentItem, notes: e.target.value })}
                    rows="2"
                  />
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onClose}
                  >
                    <X size={16} className="me-1" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-outline-primary btn-sm"
                  >
                    {isEditing ? 'Save Changes' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default BottlingItemModal;